/**
 * Static CompanyPageHeader for SSR – no hooks, no SearchContext.
 * Renders the same card when a company filter is set. "Clear" is a link.
 */
import React from 'react';
import { CompanyLogo } from './CompanyLogo';
import { CompanyName } from './CompanyName';
import type { CompanyHeaderStaticData } from '../features/srp/SrpViewModel';
import type { SrpStaticStrings } from '../features/srp/SrpViewModel';

export interface CompanyPageHeaderStaticProps {
  data: CompanyHeaderStaticData;
  strings: NonNullable<SrpStaticStrings['companyHeader']>;
}

function formatCountLabel(
  one: string,
  other: string,
  count: number
): string {
  return count === 1 ? one : other.replace('#', String(count));
}

export const CompanyPageHeaderStatic: React.FC<CompanyPageHeaderStaticProps> = ({
  data,
  strings,
}) => {
  const locationCount = data.locations.length;
  const jobCountText = formatCountLabel(
    strings.jobCountOne,
    strings.jobCountOther,
    data.jobCount
  );
  const locationCountText =
    locationCount > 0
      ? formatCountLabel(
          strings.locationCountOne,
          strings.locationCountOther,
          locationCount
        )
      : null;

  return (
    <section
      id="company-info-header"
      className="company-info-header company-header-container mb-6"
      aria-labelledby="company-header-title"
    >
      <div className="company-header-card bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 relative">
        <a
          href={data.clearFilterUrl}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
          aria-label={strings.clearFilterAria}
        >
          <svg
            className="w-5 h-5 text-gray-500 dark:text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </a>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="company-logo-wrapper flex-shrink-0">
            <CompanyLogo
              key={data.companyIdForLogo}
              companyName={data.companyIdForLogo}
              size={80}
              alt={data.name}
              variant="default"
              isTyomarkkinatori={data.isTyomarkkinatori}
              useDefaultBackgroundOnly
            />
          </div>

          <div className="flex-1 min-w-0 pr-8">
            <h1
              id="company-header-title"
              className="company-name text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1"
            >
              <CompanyName name={data.name} />
            </h1>

            <div className="company-meta flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
              {data.industry && (
                <div className="flex items-center gap-1.5">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  <span className="font-medium">{data.industry}</span>
                </div>
              )}

              <div className="flex items-center gap-1.5">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span className="font-semibold text-blue-600 dark:text-blue-400">
                  {jobCountText}
                </span>
              </div>

              {locationCountText && (
                <div className="flex items-center gap-1.5">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span>{locationCountText}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

CompanyPageHeaderStatic.displayName = 'CompanyPageHeaderStatic';
